<template>
  <div class="op-photo-tags" v-loading="loading">
    <p class="small text-muted mb-3">
      Tags from the shared photo-match dictionary (<code>analytic.photo_match_tag_dictionary</code>)
      that this operation is attached to. Filtered to this operation by default — attach/detach
      other operations from the tag editor below. Writes need this admin's user_id on
      ranking-compute's <code>PHOTO_DICT_EDITORS</code> allow-list; without it, Save/Delete/Translate
      answer 403 <code>not_editor</code>.
    </p>

    <div class="op-photo-tags__layout">
      <aside class="op-photo-tags__list">
        <el-input
          v-model="search"
          placeholder="Search: tag / question / value"
          size="small"
          class="mb-2"
          clearable
        />
        <div class="d-flex mb-2" style="gap: 8px">
          <el-select v-model="filterScope" placeholder="scope: all" size="small" clearable style="width: 50%">
            <el-option label="clinical" value="clinical" />
            <el-option label="common" value="common" />
          </el-select>
          <el-select v-model="filterShow" placeholder="show: all" size="small" clearable style="width: 50%">
            <el-option label="in form" value="true" />
            <el-option label="hidden" value="false" />
          </el-select>
        </div>
        <el-button type="primary" size="small" style="width: 100%" class="mb-2" @click="newTag">
          + New tag
        </el-button>
        <div class="small text-muted mb-2">{{ tags.length }} tag(s)</div>

        <div
          v-for="tag in tags"
          :key="tag.id"
          class="op-photo-tags__row"
          :class="{ 'op-photo-tags__row--active': tag.id === activeId }"
          @click="selectTag(tag.id)"
        >
          <div class="d-flex align-items-center" style="gap: 6px">
            <strong>{{ tag.tag || "(no tag)" }}</strong>
            <el-tag size="mini" :type="tag.scope === 'common' ? 'info' : ''">{{ tag.scope }}</el-tag>
            <el-tag v-if="!tag.show_in_form" size="mini" type="warning">hidden</el-tag>
          </div>
          <div class="small text-muted">{{ (tag.question && tag.question.en) || "" }}</div>
          <div class="small text-muted">
            {{ (tag.operation_ids || []).length }} ops · {{ (tag.values || []).length }} values · order {{ tag.order || 0 }}
          </div>
        </div>
      </aside>

      <section class="op-photo-tags__editor">
        <div v-if="!editor" class="text-muted">← Select a tag on the left, or create a new one.</div>

        <template v-else>
          <div class="row mb-3">
            <div class="col-md-4 mb-2">
              <label>tag (machine key) *</label>
              <el-input v-model="editor.tag" placeholder="machine_key" />
            </div>
            <div class="col-md-3 mb-2">
              <label>scope</label>
              <el-select v-model="editor.scope" style="width: 100%">
                <el-option label="clinical" value="clinical" />
                <el-option label="common" value="common" />
              </el-select>
            </div>
            <div class="col-md-2 mb-2">
              <label>order</label>
              <el-input-number v-model="editor.order" :min="0" style="width: 100%" />
            </div>
            <div class="col-md-3 mb-2">
              <label>&nbsp;</label>
              <div>
                <el-switch v-model="editor.showInForm" active-text="in form" inactive-text="hidden" />
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label>Operations (operation_ids)</label>
            <div class="d-flex flex-wrap mb-2" style="gap: 6px">
              <el-tag
                v-for="opId in editor.operationIds"
                :key="opId"
                closable
                @close="removeOperation(opId)"
              >
                {{ opId }} · {{ operationLabel(opId) }}
              </el-tag>
            </div>
            <el-autocomplete
              v-model="opSearch"
              :fetch-suggestions="fetchOperationSuggestions"
              placeholder="Add operation: name or id…"
              style="width: 100%"
              @select="addOperation"
            >
              <template slot-scope="{ item }">
                <span class="mr-2">{{ item.id }}</span>{{ item.name_en || item.name_ru || "(no name)" }}
                <span class="text-muted small">{{ item.kind }}</span>
              </template>
            </el-autocomplete>
            <p class="small text-muted mb-0 mt-1">
              This operation ({{ operationId }}) is added automatically for a new tag — remove it here if this
              tag should not apply to it.
            </p>
          </div>

          <div class="mb-3">
            <label>Question (14 locales)</label>
            <div class="op-photo-tags__langgrid">
              <div v-for="lang in langs" :key="lang" class="op-photo-tags__lang" :class="{ 'op-photo-tags__lang--en': lang === 'en' }">
                <label>{{ langDisplay(lang) }} · {{ lang }}</label>
                <el-input v-model="editor.question[lang]" size="small" />
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label>Values</label>
            <div v-if="!editor.values.length" class="small text-muted mb-2">No values yet — add one below.</div>
            <div v-for="(value, index) in editor.values" :key="value._key" class="op-photo-tags__value mb-2 pb-2">
              <div class="row">
                <div class="col-md-4 mb-2">
                  <label>value (machine key)</label>
                  <el-input v-model="value.value" placeholder="machine_value" />
                </div>
                <div class="col-md-7 mb-2">
                  <label>label · en</label>
                  <el-input v-model="value.label.en" placeholder="English label" />
                </div>
                <div class="col-md-1 mb-2 text-right">
                  <label>&nbsp;</label>
                  <div>
                    <el-button size="mini" type="danger" icon="el-icon-delete" @click="removeValue(index)" />
                  </div>
                </div>
              </div>
              <el-collapse>
                <el-collapse-item title="Other locales (13)">
                  <div class="op-photo-tags__langgrid">
                    <div v-for="lang in nonEnLangs" :key="lang" class="op-photo-tags__lang">
                      <label>{{ langDisplay(lang) }} · {{ lang }}</label>
                      <el-input v-model="value.label[lang]" size="small" />
                    </div>
                  </div>
                </el-collapse-item>
              </el-collapse>
            </div>
            <el-button size="mini" @click="addValue">+ Add value</el-button>
          </div>

          <div class="d-flex align-items-center" style="gap: 10px">
            <el-button type="primary" :loading="saving" @click="save">Save</el-button>
            <el-button :loading="translating" title="Fill all locales from EN via GPT-4.1" @click="doTranslate">
              Translate EN→all
            </el-button>
            <el-button v-if="activeId" type="danger" @click="del">Delete</el-button>
            <span class="small text-muted ml-auto">{{ activeId ? `id ${activeId}` : "not saved yet" }}</span>
          </div>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import APIPhotoMatchDictionary from '@/api/modules/photoMatchDictionary';

let valueKeySeq = 0;
const nextValueKey = () => {
  valueKeySeq += 1;
  return valueKeySeq;
};

export default {
  props: {
    operationId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      translating: false,
      langs: [],
      langDisplayMap: {},
      tags: [],
      search: '',
      filterScope: '',
      filterShow: '',
      activeId: null,
      editor: null,
      opSearch: '',
      opNames: {},
    };
  },
  computed: {
    nonEnLangs() {
      return this.langs.filter((lang) => lang !== 'en');
    },
  },
  watch: {
    search() {
      this.debouncedRefresh();
    },
    filterScope() {
      this.refreshList();
    },
    filterShow() {
      this.refreshList();
    },
  },
  created() {
    this.debouncedRefresh = this.debounce(this.refreshList, 250);
    this.init();
  },
  methods: {
    debounce(fn, ms) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), ms);
      };
    },
    langDisplay(lang) {
      return this.langDisplayMap[lang] || lang;
    },
    operationLabel(opId) {
      const op = this.opNames[opId];
      return (op && (op.name_en || op.name_ru)) || '…';
    },
    async init() {
      this.loading = true;
      try {
        const langsResponse = await APIPhotoMatchDictionary.getLangs();
        this.langs = langsResponse.data.langs;
        this.langDisplayMap = langsResponse.data.display || {};
        await this.refreshList();
      } catch (e) {
        this.notifyError('Failed to load the dictionary config', e);
      } finally {
        this.loading = false;
      }
    },
    async refreshList() {
      const params = { op: this.operationId };
      if (this.search.trim()) params.q = this.search.trim();
      if (this.filterScope) params.scope = this.filterScope;
      if (this.filterShow) params.show = this.filterShow;
      try {
        const response = await APIPhotoMatchDictionary.getTags(params);
        this.tags = response.data || [];
      } catch (e) {
        this.notifyError('Failed to load tags', e);
      }
    },
    async selectTag(id) {
      this.loading = true;
      try {
        const response = await APIPhotoMatchDictionary.getTag(id);
        this.openEditor(response.data);
      } catch (e) {
        this.notifyError('Failed to load the tag', e);
      } finally {
        this.loading = false;
      }
    },
    newTag() {
      this.openEditor(null);
    },
    openEditor(doc) {
      this.activeId = doc ? doc.id : null;
      const operationIds = doc
        ? [...(doc.operation_ids || [])]
        : [Number(this.operationId)];

      const question = {};
      this.langs.forEach((lang) => {
        question[lang] = (doc && doc.question && doc.question[lang]) || '';
      });

      const values = ((doc && doc.values) || []).map((value) => this.valueFromApi(value));

      this.editor = {
        tag: doc ? doc.tag || '' : '',
        scope: doc ? doc.scope || 'clinical' : 'clinical',
        showInForm: doc ? !!doc.show_in_form : true,
        order: doc ? doc.order ?? 10 : 10,
        operationIds,
        question,
        values,
      };

      this.resolveOperationNames(operationIds);
    },
    valueFromApi(value) {
      const label = {};
      this.langs.forEach((lang) => {
        label[lang] = (value.label && value.label[lang]) || '';
      });
      return { _key: nextValueKey(), value: value.value || '', label };
    },
    addValue() {
      const label = {};
      this.langs.forEach((lang) => {
        label[lang] = '';
      });
      this.editor.values.push({ _key: nextValueKey(), value: '', label });
    },
    removeValue(index) {
      this.editor.values.splice(index, 1);
    },
    async resolveOperationNames(ids) {
      const missing = ids.filter((id) => !this.opNames[id]);
      if (!missing.length) return;
      try {
        const response = await APIPhotoMatchDictionary.getOperations({ ids: missing.join(',') });
        (response.data || []).forEach((op) => {
          this.$set(this.opNames, op.id, op);
        });
      } catch (e) {
        // Names are a display nicety — a failed lookup still leaves the id chip usable.
      }
    },
    fetchOperationSuggestions(query, callback) {
      if (!query || !query.trim()) {
        callback([]);
        return;
      }
      APIPhotoMatchDictionary.getOperations({ q: query.trim() })
        .then((response) => {
          callback((response.data || []).map((op) => ({ ...op, value: `${op.id} · ${op.name_en || op.name_ru || ''}` })));
        })
        .catch(() => callback([]));
    },
    addOperation(item) {
      const id = Number(item.id);
      if (!this.editor.operationIds.includes(id)) {
        this.editor.operationIds.push(id);
        this.$set(this.opNames, id, item);
      }
      this.opSearch = '';
    },
    removeOperation(id) {
      this.editor.operationIds = this.editor.operationIds.filter((opId) => opId !== id);
    },
    collectDoc() {
      const question = {};
      this.langs.forEach((lang) => {
        const v = (this.editor.question[lang] || '').trim();
        if (v) question[lang] = v;
      });
      const values = this.editor.values
        .filter((value) => value.value && value.value.trim())
        .map((value) => {
          const label = {};
          this.langs.forEach((lang) => {
            const v = (value.label[lang] || '').trim();
            if (v) label[lang] = v;
          });
          return { value: value.value.trim(), label };
        });
      return {
        tag: this.editor.tag.trim(),
        scope: this.editor.scope,
        show_in_form: this.editor.showInForm,
        order: this.editor.order,
        operation_ids: this.editor.operationIds,
        question,
        values,
      };
    },
    async save() {
      const doc = this.collectDoc();
      if (!doc.tag) {
        this.$notify({ type: 'warning', title: 'tag is required' });
        return;
      }
      if (!doc.question.en) {
        this.$notify({ type: 'warning', title: 'An English question is required' });
        return;
      }
      this.saving = true;
      try {
        const response = this.activeId
          ? await APIPhotoMatchDictionary.updateTag(this.activeId, doc)
          : await APIPhotoMatchDictionary.createTag(doc);
        this.$notify({ type: 'success', title: `${this.activeId ? 'Updated' : 'Created'} ${response.data.tag}` });
        await this.refreshList();
        this.openEditor(response.data);
      } catch (e) {
        this.notifyError('Save failed', e);
      } finally {
        this.saving = false;
      }
    },
    async del() {
      if (!this.activeId) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete tag "${this.editor.tag || this.activeId}" permanently?`)) return;
      this.loading = true;
      try {
        await APIPhotoMatchDictionary.deleteTag(this.activeId);
        this.$notify({ type: 'success', title: 'Deleted' });
        this.activeId = null;
        this.editor = null;
        await this.refreshList();
      } catch (e) {
        this.notifyError('Delete failed', e);
      } finally {
        this.loading = false;
      }
    },
    async doTranslate() {
      const doc = this.collectDoc();
      if (!doc.question.en) {
        this.$notify({ type: 'warning', title: 'Fill in the English question first' });
        return;
      }
      this.translating = true;
      try {
        const response = await APIPhotoMatchDictionary.translate({ question: { en: doc.question.en }, values: doc.values });
        const result = response.data;
        this.langs.forEach((lang) => {
          if (result.question[lang] !== undefined) {
            this.$set(this.editor.question, lang, result.question[lang]);
          }
        });
        const byValue = {};
        (result.values || []).forEach((value) => {
          byValue[value.value] = value.label || {};
        });
        this.editor.values.forEach((value) => {
          const translated = byValue[value.value];
          if (!translated) return;
          this.langs.forEach((lang) => {
            this.$set(value.label, lang, translated[lang] || value.label[lang]);
          });
        });
        this.$notify({ type: 'success', title: 'Translated into all locales' });
      } catch (e) {
        this.notifyError('Translation failed', e);
      } finally {
        this.translating = false;
      }
    },
    notifyError(title, e) {
      const status = e && e.response && e.response.status;
      const code = e && e.response && e.response.data && e.response.data.code;
      if (403 === status && 'not_editor' === code) {
        this.$notify({
          type: 'warning',
          title: 'Not a dictionary editor',
          message: 'Ask for your user_id to be added to PHOTO_DICT_EDITORS on ranking-compute.',
        });
        return;
      }
      this.$notify({ type: 'warning', title });
    },
  },
};
</script>

<style scoped>
.op-photo-tags__layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}
.op-photo-tags__list {
  width: 320px;
  flex-shrink: 0;
  max-height: 70vh;
  overflow-y: auto;
}
.op-photo-tags__editor {
  flex: 1;
  min-width: 0;
}
.op-photo-tags__row {
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
}
.op-photo-tags__row:hover {
  background: #f4f8fb;
}
.op-photo-tags__row--active {
  background: #eef7e0;
  border-color: #a3cc0e;
}
.op-photo-tags__langgrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
}
.op-photo-tags__lang label {
  display: block;
  font-size: 12px;
  color: #909399;
  margin-bottom: 2px;
}
.op-photo-tags__lang--en label {
  color: #a3cc0e;
  font-weight: 600;
}
.op-photo-tags__value {
  border-bottom: 1px solid #ebeef5;
}
.op-photo-tags__value:last-of-type {
  border-bottom: none;
}
</style>
